import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import { fmtTime } from "../lib/dates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    start_time: "09:00",
    end_time: "17:00",
    color: "#002FA7",
    description: "",
    is_standard: false,
    min_staff: 1,
    max_staff: null,
    require_even_staff: false,
    frequency: "everyday",
    frequency_days: []
  });

  const dayLabels = [
    { value: 0, label: "Mon" },
    { value: 1, label: "Tue" },
    { value: 2, label: "Wed" },
    { value: 3, label: "Thu" },
    { value: 4, label: "Fri" },
    { value: 5, label: "Sat" },
    { value: 6, label: "Sun" },
  ];

  const frequencyLabel = (s) => {
    const f = s.frequency || "everyday";
    if (f === "everyday") return "Every day";
    if (f === "weekdays") return "Weekdays";
    if (f === "weekends") return "Weekends";
    if (f === "on_demand") return "On-demand";
    if (f === "specific_days") {
      const days = (s.frequency_days || []).map(d => dayLabels.find(x => x.value === d)?.label).filter(Boolean);
      return days.length ? days.join(", ") : "Specific days";
    }
    return "Every day";
  };

  const toggleFreqDay = (d) => {
    setFormData(prev => {
      const days = new Set(prev.frequency_days || []);
      if (days.has(d)) days.delete(d); else days.add(d);
      return { ...prev, frequency_days: Array.from(days).sort((a, b) => a - b) };
    });
  };

  const colorOptions = [
    { value: "#002FA7", label: "Blue" },
    { value: "#00C781", label: "Green" },
    { value: "#FFCC00", label: "Yellow" },
    { value: "#FF3B30", label: "Red" },
    { value: "#8B5CF6", label: "Purple" },
    { value: "#F97316", label: "Orange" },
    { value: "#52525B", label: "Gray" },
  ];

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const response = await axios.get(`${API}/shifts`, { withCredentials: true });
      setShifts(response.data);
    } catch (error) {
      console.error("Failed to fetch shifts:", error);
      toast.error("Failed to load shifts");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingShift) {
        await axios.put(`${API}/shifts/${editingShift.shift_id}`, formData, { withCredentials: true });
        toast.success("Shift updated");
      } else {
        await axios.post(`${API}/shifts`, formData, { withCredentials: true });
        toast.success("Shift created");
      }
      setDialogOpen(false);
      resetForm();
      fetchShifts();
    } catch (error) {
      console.error("Failed to save shift:", error);
      toast.error("Failed to save shift");
    }
  };

  const handleEdit = (shift) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name,
      start_time: shift.start_time,
      end_time: shift.end_time,
      color: shift.color,
      description: shift.description || "",
      is_standard: shift.is_standard,
      min_staff: shift.min_staff || 1,
      max_staff: shift.max_staff ?? null,
      require_even_staff: !!shift.require_even_staff,
      frequency: shift.frequency || "everyday",
      frequency_days: shift.frequency_days || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (shiftId) => {
    if (!window.confirm("Are you sure you want to delete this shift?")) return;
    
    try {
      await axios.delete(`${API}/shifts/${shiftId}`, { withCredentials: true });
      toast.success("Shift deleted");
      fetchShifts();
    } catch (error) {
      console.error("Failed to delete shift:", error);
      toast.error("Failed to delete shift");
    }
  };

  const resetForm = () => {
    setEditingShift(null);
    setFormData({
      name: "",
      start_time: "09:00",
      end_time: "17:00",
      color: "#002FA7",
      description: "",
      is_standard: false,
      min_staff: 1,
      max_staff: null,
      require_even_staff: false,
      frequency: "everyday",
      frequency_days: []
    });
  };

  const calculateDuration = (start, end) => {
    const [startH, startM] = start.split(":").map(Number);
    const [endH, endM] = end.split(":").map(Number);
    let hours = endH - startH;
    let mins = endM - startM;
    if (hours < 0) hours += 24;
    if (mins < 0) {
      hours -= 1;
      mins += 60;
    }
    return `${hours}h ${mins > 0 ? mins + 'm' : ''}`.trim();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Shift Types</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Configure the types of shifts available for scheduling
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
            data-testid="add-shift-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Shift
          </Button>
        </div>

        {/* Shifts Grid */}
        {shifts.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No shifts configured</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">Add shift types to start building rosters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
            {shifts.map((shift) => (
              <div key={shift.shift_id} className="bg-white dark:bg-zinc-900 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-sm"
                      style={{ backgroundColor: shift.color }}
                    />
                    <h3 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">
                      {shift.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(shift)}
                      data-testid={`edit-shift-${shift.shift_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(shift.shift_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-shift-${shift.shift_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Start</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">{fmtTime(shift.start_time)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">End</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">{fmtTime(shift.end_time)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Duration</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">
                      {calculateDuration(shift.start_time, shift.end_time)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Min Staff</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100" data-testid={`shift-min-staff-${shift.shift_id}`}>
                      {shift.min_staff || 1}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Max Staff</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100" data-testid={`shift-max-staff-${shift.shift_id}`}>
                      {shift.max_staff == null ? "Unlimited" : shift.max_staff}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Frequency</span>
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100" data-testid={`shift-frequency-${shift.shift_id}`}>
                      {frequencyLabel(shift)}
                    </span>
                  </div>
                </div>
                
                {(shift.is_standard || shift.require_even_staff) && (
                  <div className="mt-4 pt-4 border-t border-zinc-100 dark:border-zinc-800 flex flex-wrap gap-2">
                    {shift.is_standard && (
                      <span className="text-xs font-medium px-2 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded">
                        Standard Shift
                      </span>
                    )}
                    {shift.require_even_staff && (
                      <span className="text-xs font-medium px-2 py-1 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 rounded" data-testid={`shift-even-badge-${shift.shift_id}`}>
                        Even staffing
                      </span>
                    )}
                  </div>
                )}
                {shift.description && (
                  <div className={`${(shift.is_standard || shift.require_even_staff) ? 'mt-2' : 'mt-4 pt-4 border-t border-zinc-100 dark:border-zinc-800'}`}>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{shift.description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                {editingShift ? "Edit Shift" : "Add Shift"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Shift Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Morning, Night, Split"
                  className="mt-1"
                  data-testid="shift-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_time" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Start Time *
                  </Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="shift-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="end_time" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    End Time *
                  </Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="shift-end-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_staff" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Min Staff per Shift
                  </Label>
                  <Select
                    value={String(formData.min_staff)}
                    onValueChange={(v) => setFormData({ ...formData, min_staff: parseInt(v, 10) })}
                  >
                    <SelectTrigger id="min_staff" className="mt-1" data-testid="shift-min-staff-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,12,15,20].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "staff" : "staff"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                    Mandatory floor — engine will always assign at least this many
                  </p>
                </div>
                <div>
                  <Label htmlFor="max_staff" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Max Staff per Shift
                  </Label>
                  <Select
                    value={formData.max_staff === null || formData.max_staff === undefined ? "unlimited" : String(formData.max_staff)}
                    onValueChange={(v) => setFormData({ ...formData, max_staff: v === "unlimited" ? null : parseInt(v, 10) })}
                  >
                    <SelectTrigger id="max_staff" className="mt-1" data-testid="shift-max-staff-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unlimited">Unlimited</SelectItem>
                      {[1,2,3,4,5,6,7,8,9,10,12,15,20].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n} staff</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                    Cap — leave Unlimited unless you need a hard ceiling
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="frequency" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Frequency
                </Label>
                <Select
                  value={formData.frequency || "everyday"}
                  onValueChange={(v) => setFormData({ ...formData, frequency: v, frequency_days: v === "specific_days" ? formData.frequency_days : [] })}
                >
                  <SelectTrigger id="frequency" className="mt-1" data-testid="shift-frequency-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyday">Every day</SelectItem>
                    <SelectItem value="weekdays">Weekdays only (Mon–Fri)</SelectItem>
                    <SelectItem value="weekends">Weekends only (Sat–Sun)</SelectItem>
                    <SelectItem value="specific_days">Specific days of the week</SelectItem>
                    <SelectItem value="on_demand">On-demand / one-off (not auto-scheduled)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  Controls when the engine auto-fills this shift. <span className="font-medium">On-demand</span> shifts (e.g. court attendance) never appear on a generated roster — admins add them manually.
                </p>

                {formData.frequency === "specific_days" && (
                  <div className="mt-3" data-testid="shift-frequency-days">
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">Pick the days this shift runs:</p>
                    <div className="flex flex-wrap gap-2">
                      {dayLabels.map((d) => {
                        const active = (formData.frequency_days || []).includes(d.value);
                        return (
                          <button
                            key={d.value}
                            type="button"
                            onClick={() => toggleFreqDay(d.value)}
                            data-testid={`freq-day-${d.label.toLowerCase()}`}
                            className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
                              active
                                ? "bg-[#002FA7] text-white border-[#002FA7]"
                                : "bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700 hover:border-[#002FA7]"
                            }`}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="description" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Description / Operation
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Surgery operations, Front desk coverage, Emergency triage"
                  className="mt-1"
                  rows={2}
                  data-testid="shift-description-input"
                />
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  This appears in the color legend on the roster page
                </p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2 block">
                  Color
                </Label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`
                        w-8 h-8 rounded border-2 transition-all
                        ${formData.color === color.value 
                          ? 'border-zinc-900 scale-110' 
                          : 'border-transparent'
                        }
                      `}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                      data-testid={`color-${color.label.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-zinc-100 dark:border-zinc-800">
                <div>
                  <Label htmlFor="require_even_staff" className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Require Even Staffing
                  </Label>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">Always roster an even number of staff (e.g. crews working in pairs)</p>
                </div>
                <Switch
                  id="require_even_staff"
                  checked={formData.require_even_staff}
                  onCheckedChange={(checked) => setFormData({ ...formData, require_even_staff: checked })}
                  data-testid="shift-even-staff-switch"
                />
              </div>

              <div className="flex items-center justify-between py-3 border-t border-zinc-100 dark:border-zinc-800">
                <div>
                  <Label htmlFor="is_standard" className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Standard Shift
                  </Label>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">Mark as a standard shift type</p>
                </div>
                <Switch
                  id="is_standard"
                  checked={formData.is_standard}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_standard: checked })}
                  data-testid="shift-standard-switch"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 sm:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="cancel-shift-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="save-shift-btn"
                >
                  {editingShift ? "Update" : "Create"} Shift
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Shifts;
